import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Check, User, MapPin, Contact, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdmin } from '@/contexts/AdminContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const SITES = [
  { id: 'shihab_romeed', name: 'Shihab Romeed (SR)' },
  { id: 'specialist_dental_london', name: 'Specialist Dental London (SDL)' },
  { id: 'london_dental_academy', name: 'London Dental Academy (LDA)' }
];

export default function ProviderForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { apiFetch } = useAdmin();
  
  const [form, setForm] = useState({ 
    name: '', title: '', email: '', phone: '', bio: '',
    sites: [], specialties: '', isActive: true
  });
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    apiFetch(`/providers/${id}`).then(r => r.json()).then(d => {
      const p = d.provider || d;
      setForm({
        name: p.name || '',
        title: p.title || '',
        email: p.email || '',
        phone: p.phone || '',
        bio: p.bio || '',
        sites: p.sites || [],
        specialties: p.specialties ? p.specialties.join(', ') : '',
        isActive: p.isActive !== false
      });
    }).catch(() => toast.error('Failed to load provider')).finally(() => setLoading(false));
  }, [id, isEdit, apiFetch]);

  const update = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const toggleSite = (sid) => setForm(p => {
    const keys = p.sites.includes(sid) ? p.sites.filter(i => i !== sid) : [...p.sites, sid];
    return { ...p, sites: keys };
  });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    // Parse specialties
    const specArray = form.specialties.split(',').map(s => s.trim()).filter(s => s);

    const payload = { ...form, specialties: specArray };

    try {
      const res = isEdit 
        ? await apiFetch(`/providers/${id}`, { method: 'PUT', body: payload }) 
        : await apiFetch('/providers', { method: 'POST', body: payload });
        
      if (res.ok) { 
        toast.success(isEdit ? 'Provider updated successfully' : 'Provider created successfully');
        navigate('/admin/providers'); 
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || data.error || `Error ${res.status}`);
      }
    } catch(e) { 
      setError('Network error — check connection'); 
    } finally { 
      setSaving(false); 
    }
  };

  if (loading) return (
    <div className="max-w-5xl space-y-8 animate-pulse">
      <Skeleton className="h-8 w-64" />
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <Skeleton className="h-[600px] rounded-2xl" />
        <Skeleton className="h-[400px] rounded-2xl" />
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl pb-20">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/providers" className="h-10 w-10 rounded-full border border-stone-200 bg-white flex items-center justify-center hover:bg-stone-50 transition-colors shadow-sm">
          <ArrowLeft className="w-4 h-4 text-stone-600" />
        </Link>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-400">Providers</p>
          <h2 className="text-2xl md:text-3xl font-playfair font-medium text-stone-900 tracking-tight">
            {isEdit ? 'Edit Provider' : 'Create New Provider'}
          </h2>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/admin/providers')} className="h-10 bg-white border-stone-200">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !form.name || form.sites.length === 0} className="h-10 gap-2 bg-stone-900 hover:bg-stone-800 text-white px-6 shadow-md shadow-stone-900/10">
            {saving ? 'Saving...' : <><Save className="w-4 h-4"/> {isEdit ? 'Save Changes' : 'Create Provider'}</>}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-8 flex items-center gap-3 px-5 py-4 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm shadow-sm">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0 font-bold">!</span>
          <p className="flex-1">{error}</p>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-xs font-medium uppercase tracking-wider">Dismiss</button>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        
        {/* Left Column: Main Details */}
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-stone-100 rounded-lg"><User className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-lg font-medium text-stone-900">Basic Details</h3>
            </div>
            
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Full Name *</Label>
                  <Input value={form.name} onChange={e => update('name', e.target.value)} className="h-11 text-base bg-stone-50/50" placeholder="e.g. Dr. John Smith" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Title</Label>
                  <Input value={form.title} onChange={e => update('title', e.target.value)} className="h-11 bg-stone-50/50" placeholder="e.g. Specialist Periodontist" />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Bio</Label>
                <Textarea value={form.bio} onChange={e => update('bio', e.target.value)} rows={5} className="resize-y bg-stone-50/50 text-sm leading-relaxed" placeholder="Detailed biography of the provider..." />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-stone-100 rounded-lg"><Contact className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-lg font-medium text-stone-900">Contact Information</h3>
            </div>
            
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Email Address (Used for Login) *</Label>
                  <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="h-11 bg-stone-50/50" placeholder="e.g. john@example.com" />
                  {!isEdit && <p className="text-[10px] text-stone-400 mt-1.5">A temporary password will be automatically generated and emailed.</p>}
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Phone Number</Label>
                  <Input value={form.phone} onChange={e => update('phone', e.target.value)} className="h-11 bg-stone-50/50" placeholder="e.g. +44 7700 900077" />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Right Column: Settings & Config */}
        <div className="space-y-6">
          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-stone-100 rounded-lg"><MapPin className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-base font-medium text-stone-900">Site Assignment</h3>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-xs font-medium text-stone-500 mb-2 block">Available on Portals *</Label>
                <div className="space-y-2">
                  {SITES.map(s => {
                    const isSelected = form.sites.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSite(s.id)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all",
                          isSelected 
                            ? "border-stone-900 bg-stone-900 text-white shadow-sm" 
                            : "border-stone-200 bg-white hover:border-stone-300 text-stone-700"
                        )}
                      >
                        <div className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                          isSelected ? "bg-white border-white" : "border-stone-300"
                        )}>
                          {isSelected && <Check className="w-3 h-3 text-stone-900" />}
                        </div>
                        <span className="text-sm font-medium">{s.name}</span>
                      </button>
                    );
                  })}
                </div>
                {form.sites.length === 0 && <p className="text-xs text-red-500 mt-2">At least one site must be selected.</p>}
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-stone-100 rounded-lg"><Stethoscope className="w-4 h-4 text-stone-600" /></div>
              <h3 className="text-base font-medium text-stone-900">Specialties & Status</h3>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-xs font-medium text-stone-500 mb-1.5 block">Specialties</Label>
                <Input value={form.specialties} onChange={e => update('specialties', e.target.value)} className="h-11 bg-stone-50/50" placeholder="e.g. Endodontics, Implants" />
                <p className="text-[10px] text-stone-400 mt-1.5">Comma separated list of specialties.</p>
              </div>

              <div className="pt-4 border-t border-stone-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-900">Active Status</p>
                    <p className="text-xs text-stone-500 mt-0.5">Allow login and bookings</p>
                  </div>
                  <Switch checked={form.isActive} onCheckedChange={v => update('isActive', v)} />
                </div>
              </div>
            </div>
          </section>
        </div>

      </div>
    </div>
  );
}
